import React, { useEffect, useState } from 'react';
import { Phone, Mail, Globe, MapPin, Plus, Edit2, Trash2, X, DoorOpen, Wifi, ParkingCircle, User } from 'lucide-react';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { parseLocalDate } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';

export default function OverviewSection({ event, eventId, permissions }: { event: any; eventId: string; permissions: any }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [venue, setVenue] = useState<any>(null);
  const [venueContacts, setVenueContacts] = useState<any[]>([]);
  const [showClientForm, setShowClientForm] = useState(false);
  const [showVendorForm, setShowVendorForm] = useState(false);
  const [editingClientId, setEditingClientId] = useState<string | null>(null);
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null);
  const [deleteClientId, setDeleteClientId] = useState<string | null>(null);
  const [deleteVendorId, setDeleteVendorId] = useState<string | null>(null);

  const [clientForm, setClientForm] = useState({
    name: '',
    company: '',
    title: '',
    email: '',
    phone: ''
  });

  const [vendorForm, setVendorForm] = useState({
    name: '',
    company: '',
    role: '',
    email: '',
    phone: '',
    website: ''
  });

  useEffect(() => {
    loadClientsAndVendors();
  }, [eventId]);

  const loadClientsAndVendors = async () => {
    const [clientsRes, vendorsRes, venueRes] = await Promise.all([
      supabase.from('event_clients').select('*').eq('event_id', eventId),
      supabase.from('event_vendors').select('*').eq('event_id', eventId),
      supabase.from('venues').select('*').eq('event_id', eventId).limit(1).maybeSingle(),
    ]);

    if (clientsRes.data) setClients(clientsRes.data);
    if (vendorsRes.data) setVendors(vendorsRes.data);
    if (venueRes.data) {
      setVenue(venueRes.data);

      const { data: contactsData } = await supabase
        .from('venue_contacts')
        .select('*')
        .eq('venue_id', venueRes.data.id)
        .order('name');

      if (contactsData) setVenueContacts(contactsData);
    }
  };

  const handleClientSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingClientId) {
      await supabase
        .from('event_clients')
        .update(clientForm)
        .eq('id', editingClientId);
    } else {
      await supabase
        .from('event_clients')
        .insert({ ...clientForm, event_id: eventId });
    }
    resetClientForm();
    loadClientsAndVendors();
  };

  const handleVendorSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingVendorId) {
      await supabase
        .from('event_vendors')
        .update(vendorForm)
        .eq('id', editingVendorId);
    } else {
      await supabase
        .from('event_vendors')
        .insert({ ...vendorForm, event_id: eventId });
    }
    resetVendorForm();
    loadClientsAndVendors();
  };

  const deleteClient = async (id: string) => {
    await supabase.from('event_clients').delete().eq('id', id);
    setDeleteClientId(null);
    loadClientsAndVendors();
  };

  const deleteVendor = async (id: string) => {
    await supabase.from('event_vendors').delete().eq('id', id);
    setDeleteVendorId(null);
    loadClientsAndVendors();
  };

  const resetClientForm = () => {
    setClientForm({ name: '', company: '', title: '', email: '', phone: '' });
    setShowClientForm(false);
    setEditingClientId(null);
  };

  const resetVendorForm = () => {
    setVendorForm({ name: '', company: '', role: '', email: '', phone: '', website: '' });
    setShowVendorForm(false);
    setEditingVendorId(null);
  };

  const editClient = (client: any) => {
    setClientForm({
      name: client.name || '',
      company: client.company || '',
      title: client.title || '',
      email: client.email || '',
      phone: client.phone || ''
    });
    setEditingClientId(client.id);
    setShowClientForm(true);
  };

  const editVendor = (vendor: any) => {
    setVendorForm({
      name: vendor.name || '',
      company: vendor.company || '',
      role: vendor.role || '',
      email: vendor.email || '',
      phone: vendor.phone || '',
      website: vendor.website || ''
    });
    setEditingVendorId(vendor.id);
    setShowVendorForm(true);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Event Details</h3>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-zinc-500">Event Name:</p>
              <p className="text-white">{event.name}</p>
            </div>
            <div>
              <p className="text-sm text-zinc-500">Start Date:</p>
              <p className="text-white">
                {event.start_date && format(parseLocalDate(event.start_date), 'dd MMM yyyy')}
              </p>
            </div>
            <div>
              <p className="text-sm text-zinc-500">End Date:</p>
              <p className="text-white">
                {event.end_date && format(parseLocalDate(event.end_date), 'dd MMM yyyy')}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Client</h3>
            {permissions.canEdit && !showClientForm && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowClientForm(true)}
                className="h-8 text-zinc-400 hover:text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            )}
          </div>

          {showClientForm && (
            <form onSubmit={handleClientSubmit} className="space-y-3 mb-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-white">{editingClientId ? 'Edit' : 'Add'} Client</h4>
                <Button type="button" variant="ghost" size="icon" onClick={resetClientForm} className="h-6 w-6 text-zinc-500 hover:text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div>
                <Label htmlFor="client-name" className="text-xs text-zinc-400">Name *</Label>
                <Input
                  id="client-name"
                  value={clientForm.name}
                  onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                  required
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="client-company" className="text-xs text-zinc-400">Company *</Label>
                  <Input
                    id="client-company"
                    value={clientForm.company}
                    onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })}
                    required
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label htmlFor="client-title" className="text-xs text-zinc-400">Title</Label>
                  <Input
                    id="client-title"
                    value={clientForm.title}
                    onChange={(e) => setClientForm({ ...clientForm, title: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="client-email" className="text-xs text-zinc-400">Email</Label>
                  <Input
                    id="client-email"
                    type="email"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label htmlFor="client-phone" className="text-xs text-zinc-400">Phone</Label>
                  <Input
                    id="client-phone"
                    type="tel"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" size="sm" className="bg-white text-zinc-900 hover:bg-zinc-200 h-8">
                  {editingClientId ? 'Update' : 'Add'}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={resetClientForm} className="text-zinc-400 hover:text-white h-8">
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {clients.length === 0 && !showClientForm ? (
              <p className="text-sm text-zinc-500">No client contacts</p>
            ) : (
              clients.map((client) => (
                <div key={client.id} className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">
                      {client.company?.substring(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white">{client.name}</p>
                    {client.company && (
                      <p className="text-xs text-zinc-400 uppercase mt-0.5">{client.company}</p>
                    )}
                    {client.title && (
                      <p className="text-xs text-zinc-500 mt-0.5">{client.title}</p>
                    )}
                    <div className="space-y-1 mt-2">
                      {client.email && (
                        <a href={`mailto:${client.email}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-primary-400 transition-colors">
                          <Mail className="w-3.5 h-3.5" />
                          <span>{client.email}</span>
                        </a>
                      )}
                      {client.phone && (
                        <a href={`tel:${client.phone}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-primary-400 transition-colors">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{client.phone}</span>
                        </a>
                      )}
                    </div>
                  </div>
                  {permissions.canEdit && (
                    <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => editClient(client)}
                        className="h-7 w-7 text-zinc-500 hover:text-white"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteClientId(client.id)}
                        className="h-7 w-7 text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">Vendors</h3>
            {permissions.canEdit && !showVendorForm && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowVendorForm(true)}
                className="h-8 text-zinc-400 hover:text-white"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add
              </Button>
            )}
          </div>

          {showVendorForm && (
            <form onSubmit={handleVendorSubmit} className="space-y-3 mb-4 p-4 bg-zinc-800/50 rounded-lg border border-zinc-700">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-white">{editingVendorId ? 'Edit' : 'Add'} Vendor</h4>
                <Button type="button" variant="ghost" size="icon" onClick={resetVendorForm} className="h-6 w-6 text-zinc-500 hover:text-white">
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <div>
                <Label htmlFor="vendor-name" className="text-xs text-zinc-400">Name *</Label>
                <Input
                  id="vendor-name"
                  value={vendorForm.name}
                  onChange={(e) => setVendorForm({ ...vendorForm, name: e.target.value })}
                  required
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="vendor-company" className="text-xs text-zinc-400">Company *</Label>
                  <Input
                    id="vendor-company"
                    value={vendorForm.company}
                    onChange={(e) => setVendorForm({ ...vendorForm, company: e.target.value })}
                    required
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label htmlFor="vendor-role" className="text-xs text-zinc-400">Role</Label>
                  <Input
                    id="vendor-role"
                    value={vendorForm.role}
                    onChange={(e) => setVendorForm({ ...vendorForm, role: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="vendor-email" className="text-xs text-zinc-400">Email</Label>
                  <Input
                    id="vendor-email"
                    type="email"
                    value={vendorForm.email}
                    onChange={(e) => setVendorForm({ ...vendorForm, email: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
                <div>
                  <Label htmlFor="vendor-phone" className="text-xs text-zinc-400">Phone</Label>
                  <Input
                    id="vendor-phone"
                    type="tel"
                    value={vendorForm.phone}
                    onChange={(e) => setVendorForm({ ...vendorForm, phone: e.target.value })}
                    className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="vendor-website" className="text-xs text-zinc-400">Website</Label>
                <Input
                  id="vendor-website"
                  type="url"
                  value={vendorForm.website}
                  onChange={(e) => setVendorForm({ ...vendorForm, website: e.target.value })}
                  placeholder="https://"
                  className="mt-1 bg-zinc-900 border-zinc-700 text-white h-9"
                />
              </div>
              <div className="flex gap-2 pt-1">
                <Button type="submit" size="sm" className="bg-white text-zinc-900 hover:bg-zinc-200 h-8">
                  {editingVendorId ? 'Update' : 'Add'}
                </Button>
                <Button type="button" size="sm" variant="ghost" onClick={resetVendorForm} className="text-zinc-400 hover:text-white h-8">
                  Cancel
                </Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {vendors.length === 0 && !showVendorForm ? (
              <p className="text-sm text-zinc-500">No vendors</p>
            ) : (
              vendors.map((vendor) => (
                <div key={vendor.id} className="flex items-start gap-3 group">
                  <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">
                      {vendor.company?.substring(0, 2).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-white">{vendor.name}</p>
                    {vendor.company && (
                      <p className="text-xs text-zinc-400 uppercase mt-0.5">{vendor.company}</p>
                    )}
                    {vendor.role && (
                      <p className="text-xs text-zinc-500 mt-0.5">{vendor.role}</p>
                    )}
                    <div className="space-y-1 mt-2">
                      {vendor.email && (
                        <a href={`mailto:${vendor.email}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-primary-400 transition-colors">
                          <Mail className="w-3.5 h-3.5" />
                          <span>{vendor.email}</span>
                        </a>
                      )}
                      {vendor.phone && (
                        <a href={`tel:${vendor.phone}`} className="flex items-center gap-2 text-xs text-zinc-400 hover:text-primary-400 transition-colors">
                          <Phone className="w-3.5 h-3.5" />
                          <span>{vendor.phone}</span>
                        </a>
                      )}
                      {vendor.website && (
                        <a href={vendor.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-xs text-zinc-400 hover:text-primary-400 transition-colors">
                          <Globe className="w-3.5 h-3.5" />
                          <span>{vendor.website}</span>
                        </a>
                      )}
                    </div>
                  </div>
                  {permissions.canEdit && (
                    <div className="flex items-start gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => editVendor(vendor)}
                        className="h-7 w-7 text-zinc-500 hover:text-white"
                      >
                        <Edit2 className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleteVendorId(vendor.id)}
                        className="h-7 w-7 text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {venue && (
        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
          <h2 className="text-xl font-semibold text-primary-400 mb-6">{venue.name}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-medium text-white mb-4">VENUE MAP</h3>
              {venue.address ? (
                <iframe
                  src={`https://maps.google.com/maps?q=${encodeURIComponent(venue.address)}&output=embed`}
                  className="w-full aspect-video rounded-lg border border-zinc-700"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Venue Map"
                />
              ) : (
                <div className="aspect-video bg-zinc-800 rounded-lg flex items-center justify-center">
                  <MapPin className="w-8 h-8 text-zinc-600" />
                </div>
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-white mb-4">VENUE DETAILS</h3>
              <div className="space-y-3">
                {venue.address && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-zinc-300 whitespace-pre-wrap">{venue.address}</p>
                  </div>
                )}
                {venue.load_in_door && (
                  <div className="flex items-start gap-2">
                    <DoorOpen className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-zinc-500">Load-In Door</p>
                      <p className="text-sm text-zinc-300">{venue.load_in_door}</p>
                    </div>
                  </div>
                )}
                {venue.parking_info && (
                  <div className="flex items-start gap-2">
                    <ParkingCircle className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-zinc-500">Parking</p>
                      <p className="text-sm text-zinc-300">{venue.parking_info}</p>
                    </div>
                  </div>
                )}
                {venue.wifi_network && (
                  <div className="flex items-start gap-2">
                    <Wifi className="w-4 h-4 text-zinc-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-zinc-500">WiFi</p>
                      <p className="text-sm text-zinc-300">
                        {venue.wifi_network}
                        {venue.wifi_password && <span className="text-zinc-500"> / {venue.wifi_password}</span>}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {venueContacts.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-xs font-medium text-zinc-500 mb-3">VENUE CONTACTS</p>
                  <div className="space-y-3">
                    {venueContacts.map((contact) => (
                      <div key={contact.id} className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <User className="w-4 h-4 text-primary-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-white text-sm">{contact.name}</p>
                          {contact.role && (
                            <p className="text-xs text-zinc-500 mt-0.5">{contact.role}</p>
                          )}
                          <div className="flex flex-col gap-1 mt-2">
                            {contact.email && (
                              <a
                                href={`mailto:${contact.email}`}
                                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-primary-400 transition-colors"
                              >
                                <Mail className="w-3 h-3" />
                                <span>{contact.email}</span>
                              </a>
                            )}
                            {contact.phone && (
                              <a
                                href={`tel:${contact.phone}`}
                                className="flex items-center gap-1.5 text-xs text-zinc-400 hover:text-primary-400 transition-colors"
                              >
                                <Phone className="w-3 h-3" />
                                <span>{contact.phone}</span>
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {venue.notes && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-xs text-zinc-500 mb-1">Additional Notes</p>
                  <p className="text-sm text-zinc-300 whitespace-pre-wrap">{venue.notes}</p>
                </div>
              )}
            </div>
          </div>
          {permissions.canEdit && (
            <div className="mt-4 pt-4 border-t border-zinc-800">
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set('section', 'venue');
                  setSearchParams(params);
                }}
                className="text-sm text-primary-400 hover:text-primary-300 transition-colors hover:underline"
              >
                Manage venue details
              </button>
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!deleteClientId} onOpenChange={() => setDeleteClientId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Client?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this client contact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteClientId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteClientId && deleteClient(deleteClientId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteVendorId} onOpenChange={() => setDeleteVendorId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Vendor?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently remove this vendor contact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteVendorId(null)} className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteVendorId && deleteVendor(deleteVendorId)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
